import { formatDate } from '@angular/common';
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  LOCALE_ID,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  Renderer2,
  ElementRef,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbDateParserFormatter, NgbModal, NgbDateStruct, NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap';
import {
  Branch,
  CreditReason,
  Customer,
  CustomerDetails,
  CustomerVehicle,
  CUSTOMER_TYPE,
  NonCorpStore,
  OrderType,
  ORDER_TYPE,
  BrandDefaults,
  ORDER_TYPES,
  Vehicle,
} from '@pos-app/data';
import { HoldCode, MontlyDateArray, OrderHeader, OrderMessage } from '../../+state/orders.models';
import { ApiService, DialogService } from '@pos-app/core-ui';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, map, take, takeUntil } from 'rxjs/operators';
import moment from 'moment';
import { Store } from '@ngrx/store';
import { GetUserSalesPersonListAction, UpdateRetailCustomer } from '../../../customers/+state/customers.actions';
import { customersQuery } from '../../../customers/+state/customers.selectors';
import { getLocalLocaleDate } from 'libs/core-ui/src/lib/utils/utils-date';
import { FetchJdeOrder } from '../../+state/orders.actions';
import { OOESessionService } from 'libs/core-ui/src/lib/services/ooe-session.service';
import { RequestVehicleSelection, SearchString } from '../../../epc/+store/vehicles';
import { IMainState } from '../../../epc/+store';
import { Router } from '@angular/router';
import { ordersQuery } from '../../../orders/+state/orders.selectors';
import { getCombinedBranches } from 'libs/core-ui/src/lib/utils/utils-orders-combine-branches';
import { CustomersPartialState } from '../../../customers/+state/customers.reducer';

@Component({
  selector: 'app-order-header',
  templateUrl: './order-header.component.html',
  styleUrls: ['./order-header.component.scss'],
})
export class OrderHeaderComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('content') modalBox: any;
  @ViewChild('releaseOrderHoldModal')
  releaseOrderHoldModal: any;
  @ViewChild('d2') d2: NgbInputDatepicker;
  @ViewChild('changeSalespersonContent') changeSalespersonContent: any;
  @Input() orderHeader: OrderHeader;
  @Input() branchList: Branch[];
  @Input() nonCorpStoreList: NonCorpStore[];
  @Input() orderTypeList: OrderType[];
  @Input() nonCorpOrderTypeList: OrderType[];
  @Input() creditReasonList: CreditReason[];
  @Input() headerMessages: OrderMessage[];
  @Input() isExternalUser: boolean;
  @Input() isProgrammeUser: boolean;
  @Input() isVehicleDataReady: boolean;
  @Input() isCreditOrder: boolean;
  @Input() selectedReasonCode: string;
  @Input() isShowUser: boolean;
  @Input() customerType: string;
  @Input() hasCashDrawer: boolean;
  @Input() selectedCustomer: CustomerDetails;
  @Input() brandDefaults: BrandDefaults;
  @Input() allAttachments: any[];

  @Output() headersToUpdate = new EventEmitter<any>();
  @Output() useVehicleColourForAll = new EventEmitter<string>();
  @Output() selectReason = new EventEmitter<string>();
  @Output() isHeaderValid = new EventEmitter<boolean>();
  @Output() isOrderOnHold = new EventEmitter<boolean>();
  @Output() loadAllAttachments = new EventEmitter<void>();
  @Output() isAlertMessageValue = new EventEmitter<boolean>();

  form: FormGroup;
  isGarage = false;
  unSubscribe$ = new Subject<void>();
  isAddingCustomer: boolean;
  isEditingCustomer: boolean;
  isEditingShiptoCustomer: boolean;
  isSelectingCustomer: boolean;
  isAlertMessage: boolean = false;
  allHoldCodeList: HoldCode[];
  isReadOnlyOrderNote: boolean;
  isBusy: boolean;
  holdCodePassword: string;
  selectedHoldCodeToRemove: HoldCode;
  isLoading: boolean = true;
  isOrderReferenceRequired: boolean = false;
  marketingYN: any;
  marketingYNValue: any;
  orderCreateDateString: string;
  shiptoCustomer: CustomerDetails;
  isAddVehicleForCustomer = false;
  count: any;
  previousDate: any;
  datepickerVisibility: boolean = false;
  datepicker: NgbInputDatepicker;
  tempVehicle: {
    customerVehicleID: number;
    partsVehicleID: number;
    vehicleDescription: string;
    jdeVehicleID: number;
  };
  latestATPDate: string = '';
  latestATPDateValid: string = '';
  dayDetails: Array<MontlyDateArray> = [];
  selectedFitDays: Array<string> = [];
  fitDays: number;
  fitHours: number;
  customerDetailEdited: boolean = false;
  currentMonth: number;
  listFitLocations: any;
  allAllowedBranchesList: Branch[];
  isAddVehicleForGenericCustomer: boolean;
  salesPeopleList: any;

  get orderType(): string {
    return this.form.controls.orderType.value;
  }

  get orderTypes(): OrderType[] {
    const ordertypes = this.isNonCorpOrder() ? this.nonCorpOrderTypeList : this.orderTypeList;
    if (ordertypes?.length) {
      return ordertypes;
    }
    return [
      {
        OrderTypeCode: this.orderType,
        OrderTypeDescription: ORDER_TYPES.find((item) => item.OrderTypeCode === this.orderType)?.OrderTypeDescription || this.orderType,
      } as OrderType,
    ];
  }

  get selectedHoldCodeList(): HoldCode[] {
    return (this.orderHeader?.holdCodes || []).filter((item: HoldCode) => {
      return (this.allHoldCodeList || []).some((x) => x.holdCode === item.code);
    });
  }

  public get isTempVehicle(): boolean {
    return this.ooeSessionService.isTempVehicle;
  }

  public get isGenericVehicleYN(): boolean {
    return this.orderHeader?.genericVehicleTF;
  }

  public get isGenericQuotesOnlyTF(): boolean {
    return this.ooeSessionService.isGenericQuotesOnlyTF;
  }

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private formatter: NgbDateParserFormatter,
    private modalService: NgbModal,
    private toastr: ToastrService,
    private store: Store<IMainState>,
    private dialogService: DialogService,
    private ooeSessionService: OOESessionService,
    private router: Router,
    private renderer: Renderer2,
    private customerStore: Store<CustomersPartialState>,
    @Inject(LOCALE_ID) public locale: string
  ) {
    this.form = this.fb.group({
      customerName: [{ value: '', disabled: true }, [Validators.email, Validators.required]],
      customerNumber: [''],
      branch: ['', Validators.required],
      orderType: ['', [Validators.required]],
      vehicle: [{ value: '', disabled: true }, Validators.required],
      reference: [''],
      orderNumber: [{ value: '', disabled: true }, Validators.required],
      requestDate: ['', Validators.required],
      customerVehicleID: [''],
      partsVehicleID: [''],
      currencyCode: [{ value: '', disabled: true }],
      vehicleColour: [{ value: '', disabled: true }],
      quickSaleYN: [''],
      customerPaymentTerms: [''],
      customerTaxRate: [''],
      shipToNumber: ['', Validators.required],
      shipToName: [''],
      creditReason: [null],
      nonCorpStoreSaleYN: ['N'],
      holdCodes: [null],
      marketingOptInYNFromOrder: [''],
      fitLocation: [''],
    });
  }

  ngOnInit() {
    this.apiService
      .listHoldCodes()
      .pipe(
        takeUntil(this.unSubscribe$),
        finalize(() => (this.isLoading = false))
      )
      .subscribe((holdCodes) => {
        this.allHoldCodeList =
          this.isExternalUser || this.isProgrammeUser ? holdCodes.SearchResults.filter((x) => x.readOnlyYN === 'N') : holdCodes.SearchResults;
      });
    if (this.isProgrammeUser) {
      this.apiService
        .listFitLocations()
        .pipe(
          takeUntil(this.unSubscribe$),
          finalize(() => (this.isLoading = false))
        )
        .subscribe((fitLocations) => {
          this.listFitLocations = fitLocations.SearchResults;
        });
    }

    this.store
      .select(ordersQuery.getStagingOrder)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((orderDetails) => {
        if (orderDetails?.length > 0) {
          const dates = orderDetails?.map((el) => el.atpDescription)?.filter((i) => /^\d+\/\d+ - [A-Z]\w/.test(i));
          if (dates.length > 0) {
            this.latestATPDate = this.filterAndSelectDatesWithText(dates);
            // when orderHeader workshopBookingTF is true and workshopDateConfirmedTF  is false then fitting date will be latest ATP date;
            if (this.orderHeader?.workshopBookingTF && !this.orderHeader?.workshopDateConfirmedTF) {
              //if  apt month is less than today month then it will be considered next year
              const currentMonth = new Date().getMonth();
              const currentYear = new Date().getFullYear();
              this.latestATPDateValid =
                this.filterAndSelectDatesWithText(dates)?.split('-')[0].trim() +
                '/' +
                (this.filterAndSelectDatesWithText(dates)?.split('-')[0].trim()?.split('/')[1] < currentMonth ? currentYear + 1 : currentYear);

              this.form.patchValue({
                requestDate: this.latestATPDateValid,
              });
            }
          }
        }
      });

    Object.keys(this.form.controls).forEach((key) => {
      if (key === 'branch' || key === 'orderType') {
        this.form
          .get(key)
          .valueChanges.pipe(takeUntil(this.unSubscribe$))
          .subscribe((val) => {
            if (val) {
              if ((val === ORDER_TYPE.creditPrice || val === ORDER_TYPE.creditReturn) && !this.hasCashDrawer) {
                this.form.controls.orderType.setErrors({
                  invalid: true,
                });
                this.toastr.error('Cannot process a credit without a register connected');
              } else {
                if (key == 'branch') {
                  if (this.branchList.some((store) => store.BranchCode.toString() === this.form.controls.branch.value)) {
                    // check if the branch is corporative branch
                    this.form.patchValue(
                      {
                        shipToName: this.orderHeader?.customerName,
                        shipToNumber: this.orderHeader?.customerNumber,
                        branch: this.form.controls.branch.value,
                        nonCorpStoreSaleYN: 'N',
                      },
                      { emitEvent: false }
                    );
                  } else {
                    // if user selects a non-corp store
                    this.form.patchValue(
                      {
                        shipToName:
                          this.allAllowedBranchesList.find((store) => store.BranchCode === this.form.controls.branch.value)?.BranchDescription || '',
                        nonCorpStoreSaleYN: 'Y',
                        shipToNumber: this.form.controls.branch.value,
                      },
                      { emitEvent: false }
                    );
                  }
                }
                this.form.controls.orderType.setErrors(null);
                this.updateHeaders();
              }
            }
          });
      }
    });

    this.form
      .get('reference')
      .valueChanges.pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.unSubscribe$))
      .subscribe((val) => {
        this.addRemoveValidationFromReference(this.selectedCustomer);

        if (this.isValidatorExist('reference')) {
          if (!val) {
            this.createEmptyFieldErrorMessage(val, 'reference');
          }
        } else {
          this.form.controls.reference.setErrors(null);
          this.isOrderReferenceRequired = false;
        }
        if (val && this.orderHeader?.reference !== val) {
          this.updateHeaders();
          this.form.controls.reference.setErrors(null);
          this.isOrderReferenceRequired = false;
        }
        this.isHeaderValid.emit(this.form.valid);
      });

    this.form
      .get('creditReason')
      .valueChanges.pipe(takeUntil(this.unSubscribe$))
      .subscribe((value) => {
        this.selectReason.emit(value);
      });

    this.form
      .get('requestDate')
      .valueChanges.pipe(
        distinctUntilChanged(),
        map((value) => {
          const requestedDate = this.formatter.parse(value);
          if (isNaN(requestedDate?.day) || isNaN(requestedDate?.month) || isNaN(requestedDate?.year)) {
            this.form.get('requestDate').setErrors({ invalid: true }, { emitEvent: false });
            return false;
          }
          const momentDate = moment(
            `${requestedDate.day}/${requestedDate.month}/${requestedDate.year < 100 ? 2000 + requestedDate.year : requestedDate.year}`,
            'D/M/YYYY',
            true
          );
          const today = moment().startOf('day');
          if (!momentDate.isValid() || momentDate.isBefore(today)) {
            this.toastr.error('Invalid request date.');
            this.form.get('requestDate').setErrors({ invalid: true }, { emitEvent: false });
            return false;
          }
          this.form.get('requestDate').setErrors({ invalid: false }, { emitEvent: false });
          return true;
        }),
        takeUntil(this.unSubscribe$)
      )
      .subscribe((res) => {
        if (!this.orderHeader?.workshopBookingTF) {
          this.isHeaderValid.emit(this.form.valid);
          if (res) {
            this.updateHeaders();
          }
        }
      });

    this.form
      .get('shipToNumber')
      .valueChanges.pipe(takeUntil(this.unSubscribe$))
      .subscribe((shipToNumber) => {
        if ((this.isExternalUser || this.isProgrammeUser) && this.form.controls.orderType.value === ORDER_TYPE.arbOrder) {
          if (this.branchList.some((store) => store.BranchCode.toString() === this.form.controls.branch.value)) {
            // check if the branch is corporative branch
            this.form.patchValue(
              {
                shipToName: this.orderHeader?.customerName,
                shipToNumber: this.orderHeader?.customerNumber,
                branch: this.form.controls.branch.value,
                nonCorpStoreSaleYN: 'N',
              },
              { emitEvent: false }
            );
          } else {
            // if user selects a non-corp store
            this.form.patchValue({
              shipToName: this.allAllowedBranchesList.find((store) => store.BranchCode === this.form.controls.branch.value)?.BranchDescription || '',
            });
          }
        } else {
          this.form.patchValue(
            {
              shipToName: this.nonCorpStoreList.find((store) => store.customerNumber === shipToNumber)?.customerName || '',
            },
            { emitEvent: false }
          );
        }
        this.updateHeaders();
      });

    this.form
      .get('holdCodes')
      .valueChanges.pipe(takeUntil(this.unSubscribe$))
      .subscribe((holdCodeValue) => {
        if (holdCodeValue && !this.selectedHoldCodeList.some((item) => item.holdCode === holdCodeValue.code)) {
          this.updateHeaders(holdCodeValue);
          this.form.controls.holdCodes.setValue(null, { emitEvent: false });
        }
      });

    this.form
      .get('marketingOptInYNFromOrder')
      .valueChanges.pipe(takeUntil(this.unSubscribe$))
      .subscribe((marketingOptInYN) => {
        this.marketingYNValue = marketingOptInYN;
        this.checkMarketingOpt(marketingOptInYN);
      });

    this.store
      .select(customersQuery.getAlertMessage)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((alertMessage) => {
        if (alertMessage && alertMessage.alertUpdatedDate !== ' ' && alertMessage.alertUpdatedTime !== 0) {
          this.isAlertMessage = true;
          this.isAlertMessageValue.emit(true);
        }
      });

    this.form
      .get('fitLocation')
      .valueChanges.pipe(takeUntil(this.unSubscribe$))
      .subscribe((fitLocation) => {
        this.updateHeaders(fitLocation);
        this.form.controls.fitLocation.setValue(fitLocation, { emitEvent: false });
      });
    this.selectedFitDays = this.orderHeader?.fitDateArray;
  }

  ngOnDestroy() {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    //Check whether selected customer required reference field or not
    //If required add the validation
    if (
      (changes.selectedCustomer && this.selectedCustomer && this.form && !changes.selectedCustomer.firstChange) ||
      //&&
      //Number(this.selectedCustomer.CustomerNumber) == Number(this.form.value['customerNumber'])
      ((this.isExternalUser || this.isProgrammeUser) && this.orderHeader?.orderType === ORDER_TYPE.quoteOrder && this.selectedCustomer)
    ) {
      this.addRemoveValidationFromReference(this.selectedCustomer, false);

      if (this.isValidatorExist('reference')) {
        if (this.form?.value['reference']?.length === 0) {
          document?.getElementById('referenceInput')?.focus();
        }
      } else {
        this.form.controls.reference.setErrors(null);
        this.isOrderReferenceRequired = false;
      }
    }

    if (changes.customerType && changes.customerType.currentValue && this.form) {
      this.customerType === CUSTOMER_TYPE.retail ||
      (this.orderHeader?.orderType === ORDER_TYPE.quoteOrder && (!this.isExternalUser || !this.isProgrammeUser))
        ? this.form.controls.reference.clearValidators()
        : this.form.controls.reference.addValidators([Validators.required, Validators.pattern(/^(\s+\S+\s*)*(?!\s).*$/), Validators.minLength(1)]);
      this.form.controls.reference.updateValueAndValidity({ emitEvent: false });
      this.isHeaderValid.emit(this.form.valid);
    }

    if (changes.orderHeader && changes.orderHeader.currentValue) {
      this.orderCreateDateString = getLocalLocaleDate(this.orderHeader.orderCreateDateString, this.locale);

      if (this.form) {
        const orderTypeGroup = (this.orderTypeList || this.nonCorpOrderTypeList).find(
          (item) => item.OrderTypeCode === this.orderHeader?.orderType
        )?.OrderTypeGroup;
        if (orderTypeGroup) {
          this.orderTypeList = this.orderTypeList.filter((item) => item.OrderTypeGroup === orderTypeGroup);
          this.nonCorpOrderTypeList = this.nonCorpOrderTypeList.filter((item) => item.OrderTypeGroup === orderTypeGroup);
        }

        this.form.patchValue(
          {
            customerName: this.orderHeader.customerName,
            customerNumber: this.orderHeader.customerNumber,
            reference: this.orderHeader.reference.trim(),
            orderNumber: this.orderHeader.orderNumber,
            vehicle: this.orderHeader.vehicleDescription,
            orderType: this.orderHeader.orderType.trim() ? this.orderHeader.orderType.trim() : null,
            branch: this.orderHeader.branch,
            requestDate: getLocalLocaleDate(this.orderHeader.requestDateString, this.locale),
            customerVehicleID: this.orderHeader.customerVehicleID,
            partsVehicleID: this.orderHeader.partsVehicleID,
            currencyCode: this.orderHeader.currencyCode,
            quickSaleYN: this.orderHeader.quickSaleYN,
            customerPaymentTerms: this.orderHeader.customerPaymentTerms,
            customerTaxRate: this.orderHeader.customerTaxRate,
            shipToNumber: this.orderHeader.shipToNumber || null,
            shipToName: this.orderHeader.shipToName || null,
            nonCorpStoreSaleYN: this.orderHeader.nonCorpStoreSaleYN,
            vehicleColour: this.orderHeader.vehicleColour,
            fitLocation: this.orderHeader.fitLocation,
          },
          { emitEvent: false }
        );
        if (this.orderHeader.readOnlyYN === 'N' && this.orderHeader.requestDateString) {
          const requestedDate = this.formatter.parse(this.orderHeader.requestDateString);
          const momentDate = moment(
            `${requestedDate.day}/${requestedDate.month}/${requestedDate.year < 100 ? 2000 + requestedDate.year : requestedDate.year}`,
            'D/M/YYYY',
            true
          );
          const today = moment().startOf('day');
          if (!momentDate.isValid() || momentDate.isBefore(today)) {
            this.form.controls['requestDate'].patchValue(getLocalLocaleDate(today.format('DD/MM/YYYY'), this.locale), { emitEvent: false });
          } else {
            if (this.orderHeader?.workshopBookingTF && this.selectedFitDays?.filter((el) => el === this.orderHeader.requestDateString).length < 1) {
              this.selectedFitDays = [...this.selectedFitDays, this.orderHeader.requestDateString];
            }
          }
        }

        if (changes.orderHeader.previousValue && changes.orderHeader.currentValue.quickSaleYN !== changes.orderHeader.previousValue.quickSaleYN) {
          this.orderHeader.quickSaleYN === 'Y'
            ? this.form.controls.branch.disable({ emitEvent: false })
            : this.form.controls.branch.enable({ emitEvent: false });
          this.orderHeader.quickSaleYN === 'Y'
            ? this.form.controls.orderType.disable({ emitEvent: false })
            : this.form.controls.orderType.enable({ emitEvent: false });
          this.orderHeader.quickSaleYN === 'Y'
            ? this.form.controls.shipToNumber.disable({ emitEvent: false })
            : this.form.controls.shipToNumber.enable({ emitEvent: false });
        } else if (this.isExternalUser || (this.isProgrammeUser && this.orderHeader.orderType === ORDER_TYPE.arbOrder)) {
          this.form.controls.shipToNumber.enable({ emitEvent: false });
        } else if (
          (!changes.orderHeader.previousValue && (this.orderHeader.quickSaleYN === 'Y' || !this.orderHeader.orderNumber.startsWith('T'))) ||
          this.isExternalUser ||
          this.isProgrammeUser
        ) {
          this.form.controls.branch.disable({ emitEvent: false });
          this.form.controls.orderType.disable({ emitEvent: false });
          this.form.controls.shipToNumber.disable({ emitEvent: false });
        }

        this.isHeaderValid.emit(this.form.valid);
      }

      if (this.isGenericVehicleYN) {
        this.tempVehicle = {
          customerVehicleID: this.orderHeader.customerVehicleID,
          partsVehicleID: this.orderHeader.partsVehicleID,
          vehicleDescription: this.orderHeader.vehicleDescription,
          jdeVehicleID: this.orderHeader.jdeVehicleID,
        };
      }
    }

    if (changes.selectedReasonCode && changes.selectedReasonCode.currentValue && this.form) {
      this.form.patchValue(
        {
          creditReason: this.selectedReasonCode,
        },
        { emitEvent: false }
      );
    }

    if (
      (this.isExternalUser || this.isProgrammeUser) &&
      this.orderHeader?.orderType !== ORDER_TYPE.arbOrder &&
      this.orderHeader?.orderType !== ORDER_TYPE.openArbOrder
    ) {
      this.form.controls.shipToNumber.disable({ emitEvent: false });
    }

    if ((changes.nonCorpStoreList || changes.orderHeader) && this.isNonCorpOrder()) {
      if (this.nonCorpStoreList && this.orderHeader) {
        this.updateNonCorpStoreList();
      }
    }

    if (
      changes.orderHeader &&
      (this.orderHeader.orderType === ORDER_TYPE.arbOrder || this.orderHeader?.orderType === ORDER_TYPE.openArbOrder) &&
      (this.isExternalUser || this.isProgrammeUser)
    ) {
      this.updateBranchList();
    }
    this.isOrderOnHold.emit(this.selectedHoldCodeList?.length > 0);
    this.marketingYN = this.selectedCustomer?.MarketingYN;
    if (this.headerMessages?.length > 0 && this.headerMessages.some((el) => el.msgType === 'ERROR')) {
      this.isHeaderValid.emit(false);
    }
  }

  removeHoldCode(code: HoldCode) {
    this.selectedHoldCodeToRemove = code;
    if (code.savedYN === 'Y') {
      this.modalService.open(this.releaseOrderHoldModal, {
        backdrop: 'static',
      });
    } else {
      this.apiService
        .releaseHoldCode({
          holdCode: code.holdCode,
          password: '',
        })
        .pipe(takeUntil(this.unSubscribe$))
        .subscribe((res) => {
          this.updateHeaders(code);
        });
    }
  }

  onRemoveHoldCode() {
    this.isBusy = true;
    this.apiService
      .releaseHoldCode({
        holdCode: this.selectedHoldCodeToRemove.holdCode,
        password: this.holdCodePassword,
      })
      .pipe(finalize(() => (this.isBusy = false)))
      .subscribe(
        (res) => {
          this.updateHeaders(this.selectedHoldCodeToRemove);
          this.closeHoldCodeModal();
        },
        (error) => {
          this.toastr.error(error.ErrorMessage);
        }
      );
  }

  closeHoldCodeModal() {
    this.holdCodePassword = '';
    this.modalService.dismissAll();
  }

  updateHeaders(holdCodeToUpdate?, reloadCustomer = false, reloadUserState = false) {
    const requestedDate = this.formatter.parse(this.form.controls.requestDate.value);
    let shipToName;
    if ((this.isExternalUser || this.isProgrammeUser) && this.form.controls.orderType.value === 'SQ') {
      shipToName = this.orderHeader?.shipToName?.trim();
    } else {
      shipToName = this.form.controls.shipToName.value?.trim();
    }
    //if user is extrnal or  programme user and if branch is changed when ordertype is ZZ then check corporate store or non corporate store
    if (
      (this.isExternalUser || this.isProgrammeUser) &&
      (this.form.controls.orderType.value === 'ZZ' || this.form.controls.orderType.value === 'SF')
    ) {
      if (this.form.controls.branch.value !== this.orderHeader.branch) {
        // check if the branch is corporative or non corporative branch
        this.form.patchValue(
          {
            nonCorpStoreSaleYN: this.isNonCorpOrder() ? 'N' : 'Y',
          },
          { emitEvent: false }
        );
      }
    }

    const newHeaders = {
      reference: this.form.controls.reference.value?.trim(),
      branch: this.form.controls.branch.value,
      orderType: this.form.controls.orderType.value,
      customerVehicleID: this.form.controls.customerVehicleID.value,
      partsVehicleID: this.form.controls.partsVehicleID.value,
      requestDateString: formatDate(
        new Date(requestedDate.year < 100 ? 2000 + requestedDate.year : requestedDate.year, requestedDate.month - 1, requestedDate.day),
        'dd/MM/yyyy',
        'en-au'
      ),
      currencyCode: this.orderHeader.currencyCode,
      vehicleDescription: this.form.controls.vehicle.value,
      customerNumber: Number(this.form.controls.customerNumber.value),
      customerName: this.form.controls.customerName.value,
      quickSaleYN: this.form.controls.quickSaleYN.value,
      customerPaymentTerms: this.form.controls.customerPaymentTerms.value,
      customerTaxRate: this.form.controls.customerTaxRate.value,
      shipToNumber: this.form.controls.shipToNumber.value,
      shipToName: shipToName,
      nonCorpStoreSaleYN: this.form.controls.nonCorpStoreSaleYN.value,
      holdCode: holdCodeToUpdate ? holdCodeToUpdate.code : '',
      holdCodeDescription: holdCodeToUpdate ? holdCodeToUpdate.description : '',
      fitDateArray: this.selectedFitDays.length > 1 ? this.selectedFitDays : [],
      fitLocation: this.form.controls.fitLocation.value,
    };

    for (const key in newHeaders) {
      if (Object.prototype.hasOwnProperty.call(newHeaders, key)) {
        const newValue = newHeaders[key];
        const oldValue = this.orderHeader[key];

        if (key !== 'holdCode' && key !== 'holdCodeDescription') {
          if (newValue === oldValue) {
            delete newHeaders[key];
          }
        } else if (!newValue) {
          delete newHeaders[key];
        }
      }
    }
    if (this.isExternalUser || this.isProgrammeUser) {
      if (this.form.controls.orderType.value === 'SQ') {
        delete newHeaders.customerNumber;
      } else if (this.form.controls.orderType.value === 'ZZ' || this.form.controls.orderType.value === 'SF') {
        if (this.form.controls.nonCorpStoreSaleYN.value === 'Y') {
          delete newHeaders.branch;
        } else if (this.form.controls.nonCorpStoreSaleYN.value === 'N') {
          newHeaders['branch'] = this.form.controls.branch.value;
        }
      }
    }

    const data = { ...newHeaders, orderNumber: this.form.controls.orderNumber.value };

    this.headersToUpdate.emit({ data, reloadCustomer, reloadUserState });
  }

  openEditVehicleModal() {
    this.isGarage = true;
    this.isAddingCustomer = false;
    this.isEditingCustomer = false;
    this.isEditingShiptoCustomer = false;
    this.isSelectingCustomer = false;

    let searchLabel = '';
    if (this.form.controls['vehicle'].value?.length > 0) {
      const values: string[] = this.form.controls['vehicle'].value.split(' ');
      if (values?.length > 1 && (values[0].trim() || values[1].trim())) {
        searchLabel = values[0] + ' ' + values[1];
      } else {
        searchLabel = '';
      }
    }

    this.store.dispatch(SearchString({ payload: searchLabel }));

    this.openModalBox();
  }

  openNotesModal() {
    this.isGarage = false;
    this.isAddingCustomer = false;
    this.isEditingCustomer = false;
    this.isEditingShiptoCustomer = false;
    this.isSelectingCustomer = false;
    this.isReadOnlyOrderNote = this.orderHeader.readOnlyYN === 'Y';
    this.openModalBox();
  }

  selectCustomerVehicle(selectedVehicle: CustomerVehicle) {
    this.form.patchValue(
      {
        customerVehicleID: selectedVehicle.customerVehicleID,
        partsVehicleID: selectedVehicle.partsVehicleID,
        vehicle: selectedVehicle.ShortDescription,
        vehicleColour: selectedVehicle.ColourCode,
      },
      { emitEvent: false }
    );
    this.modalService.dismissAll();
    this.isGarage = false;
    this.isAddVehicleForCustomer = false;
    this.updateHeaders(null, false, true);
  }

  onSelectVehicle(vehicle: Vehicle) {
    this.form.patchValue(
      {
        customerVehicleID: vehicle.VehicleID,
        partsVehicleID: vehicle.partsVehicleID,
        vehicle: `${vehicle.Make} ${vehicle.Model} ${vehicle.Series}`,
      },
      { emitEvent: false }
    );
    if (this.isGenericVehicleYN) {
      this.isAddVehicleForCustomer = true;
      this.isAddVehicleForGenericCustomer = true;
      this.tempVehicle = {
        customerVehicleID: Number(vehicle.VehicleID),
        partsVehicleID: Number(vehicle.partsVehicleID),
        vehicleDescription: `${vehicle.Make} ${vehicle.Model} ${vehicle.Series}`,
        jdeVehicleID: Number(vehicle.VehicleID),
      };
    } else {
      this.modalService.dismissAll();
      this.isGarage = false;
      this.updateHeaders();
    }
  }

  public savedVehicleForGenericCustomer(event) {
    if (event) {
      this.store.select(customersQuery.getCustomerVehicleID).subscribe((res) => {
        if (res) {
          this.form.patchValue(
            {
              customerVehicleID: res,
            },
            { emitEvent: false }
          );
          this.isGarage = false;
          this.isAddVehicleForGenericCustomer = false;
          this.isAddVehicleForCustomer = false;
          this.modalService.dismissAll();
          this.updateHeaders();
        }
      });
    }
  }

  clearVehicle() {
    this.form.patchValue(
      {
        customerVehicleID: '0',

        partsVehicleID: '0',
        vehicle: ' ',
        vehicleColour: '',
      },
      { emitEvent: false }
    );
    this.store.dispatch(
      RequestVehicleSelection({
        payload: null,
      })
    );
    this.updateHeaders();
  }

  colourAll() {
    this.useVehicleColourForAll.emit();
  }

  addNewCustomer() {
    this.isAddingCustomer = true;
    this.isGarage = false;
    this.isEditingCustomer = false;
    this.isEditingShiptoCustomer = false;
    this.isSelectingCustomer = false;
    this.openModalBox();
  }

  editCustomer() {
    if ((this.isExternalUser || this.isProgrammeUser) && this.form.controls.orderType.value !== 'SQ') {
      return;
    }

    this.customerDetailEdited = true;
    this.isEditingCustomer = true;
    this.isGarage = false;
    this.isAddingCustomer = false;
    this.isEditingShiptoCustomer = false;
    this.isSelectingCustomer = false;
    this.openModalBox();
  }

  editShipToCustomer() {
    if (this.orderHeader.shipToNumber === this.orderHeader.customerNumber) {
      this.editCustomer();
      return;
    }

    this.apiService
      .getCustomerDetails({
        customerNumberSearch: this.orderHeader.shipToNumber,
      })
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((res) => {
        if (res?.SearchResults[0]) {
          this.shiptoCustomer = res.SearchResults[0];
          this.isEditingShiptoCustomer = true;
          this.isGarage = false;
          this.isAddingCustomer = false;
          this.isEditingCustomer = false;
          this.isSelectingCustomer = false;

          this.openModalBox();
        }
      });
  }

  editOriginalOrder() {
    if (this.orderHeader?.originalOrder) {
      const originalOrderArr = this.orderHeader.originalOrder.split(' ');

      const orderNumber = originalOrderArr[0];
      const orderType = originalOrderArr[1];

      this.dialogService
        .confirm(`Are you sure you want to park the current order and load the original order?`)
        .pipe(takeUntil(this.unSubscribe$))
        .subscribe((res) => {
          if (res) {
            this.store.dispatch(
              new FetchJdeOrder({
                orderNumber,
                orderType,
              })
            );
          }
        });
    }
  }

  checkMarketingOpt(marketingOptInYN: any) {
    if (
      this.orderHeader?.quickSaleYN === 'N' &&
      (this.selectedCustomer?.Email === '' || this.selectedCustomer?.PostCode === '') &&
      marketingOptInYN
    ) {
      this.editCustomer();
    } else if (this.orderHeader?.quickSaleYN === 'Y') {
      this.form.patchValue(this.getValuesToPatchMarketingOpt(marketingOptInYN), {
        emitEvent: false,
      });
      this.addNewCustomer();
    } else {
      this.store.dispatch(
        new UpdateRetailCustomer({
          customerNumber: this.selectedCustomer?.CustomerNumber,
          branch: this.selectedCustomer?.BranchCode,
          firstName: this.selectedCustomer?.FirstName,
          surname: this.selectedCustomer?.Surname,
          email: this.selectedCustomer?.Email,
          phone: this.selectedCustomer?.Phone,
          phoneType: this.selectedCustomer?.PhoneType,
          addressLine1: this.selectedCustomer?.AddressLine1,
          addressLine2: this.selectedCustomer?.AddressLine2,
          suburb: this.selectedCustomer?.Suburb,
          state: this.selectedCustomer?.StateCode,
          postCode: this.selectedCustomer?.PostCode,
          country: this.selectedCustomer?.CountryCode,
          secContactName: this.selectedCustomer?.SecondaryContact,
          secContactPhone: this.selectedCustomer?.SecondaryContactPhone,
          marketingOptInYN: marketingOptInYN ? 'Y' : !marketingOptInYN ? 'N' : '',
          useBusinessNameYN: this.selectedCustomer?.UseBusinessNameYN,
        })
      );

      this.apiService
        .getCustomerDetails({
          customerNumberSearch: this.selectedCustomer?.CustomerNumber,
        })
        .pipe(takeUntil(this.unSubscribe$))
        .subscribe((res) => {
          this.selectedCustomer = res.SearchResults[0];
          this.form.patchValue(this.getValuesToPatchMarketingOptIn(res.SearchResults[0]), {
            emitEvent: false,
          });
          this.updateHeaders(null, true);
        });
    }
  }

  fetchCustomerDetails(customerNumber) {
    this.modalService.dismissAll();

    if (customerNumber) {
      this.apiService
        .fetchCustomerDetails({
          customerNumber,
        })
        .pipe(takeUntil(this.unSubscribe$))
        .subscribe((res) => {
          const customer = res.SearchResults[0];
          if (
            this.isEditingCustomer ||
            this.isAddingCustomer ||
            this.customerDetailEdited ||
            this.orderHeader.customerNumber !== customer.customerNumber
          ) {
            this.form.patchValue(this.getValuesToPatch(customer), {
              emitEvent: false,
            });
            this.customerDetailEdited = false;
          }
          if (this.isEditingShiptoCustomer) {
            this.form.patchValue(
              {
                shipToNumber: customer.CustomerNumber,
                shipToName: customer.CustomerName,
              },
              {
                emitEvent: false,
              }
            );
          }
          this.isEditingCustomer = false;
          this.isAddingCustomer = false;
          this.isEditingShiptoCustomer = false;
          this.updateHeaders(null, true);
        });
    }
  }

  selectNewCustomer() {
    this.isSelectingCustomer = true;
    this.isGarage = false;
    this.isAddingCustomer = false;
    this.isEditingCustomer = false;
    this.isEditingShiptoCustomer = false;

    this.openModalBox();
  }

  handleSelectCustomer(customer: Customer) {
    if (customer) {
      this.isSelectingCustomer = false;
      this.modalService.dismissAll();

      if (customer.customerType === CUSTOMER_TYPE.shipTo) {
        this.apiService
          .fetchCustomerBillTo({
            customerShipTo: customer.customerNumber,
          })
          .pipe(takeUntil(this.unSubscribe$))
          .subscribe((result) => {
            let valuesToPatch = this.getValuesToPatch(customer);
            if (result) {
              if (result.SearchResults[0].billToNumber)
                valuesToPatch = {
                  ...valuesToPatch,
                  quickSaleYN: 'N',
                  customerNumber: result.SearchResults[0].billToNumber,
                  customerName: result.SearchResults[0].billToName,
                };
              this.form.patchValue(valuesToPatch, { emitEvent: false });
              this.updateHeaders();
            }
          });
      } else if (customer.customerType === CUSTOMER_TYPE.billTo) {
        let valuesToPatch = this.getValuesToPatch(customer);
        valuesToPatch = { ...valuesToPatch, quickSaleYN: 'N' };
        this.form.patchValue(valuesToPatch, { emitEvent: false });
        this.updateHeaders();
      } else {
        this.apiService
          .fetchCashCustomer({ branch: this.orderHeader.branch })
          .pipe(takeUntil(this.unSubscribe$))
          .subscribe((res) => {
            let valuesToPatch = this.getValuesToPatch(customer);

            // check if selected customer is a quicksale customer
            if (customer.customerNumber === res.customerNumber.toString()) {
              valuesToPatch = { ...valuesToPatch, quickSaleYN: 'Y' };
            }

            if (this.isGenericVehicleYN) {
              valuesToPatch = {
                ...valuesToPatch,
                customerVehicleID: this.orderHeader.customerVehicleID,
                partsVehicleID: this.orderHeader.partsVehicleID,
                vehicle: this.orderHeader.vehicleDescription,
                vehicleColour: this.orderHeader.vehicleColour,
              };
            }

            this.form.patchValue(valuesToPatch, { emitEvent: false });
            this.updateHeaders();

            if (this.form.controls.vehicle.value && this.form.controls.vehicle.value?.trim()?.length > 0) {
              this.isAddVehicleForCustomer = false;
              this.openEditVehicleModal();
            } else {
              this.isAddVehicleForCustomer = false;
            }
          });
      }
    }
  }

  getValuesToPatchMarketingOpt(value: Boolean) {
    return { marketingOptInYNFromOrder: value ? 'Y' : 'N' };
  }
  getValuesToPatchMarketingOptIn(customerDetail: CustomerDetails) {
    return {
      marketingOptInYNFromOrder: customerDetail?.MarketingYN ? '' : customerDetail?.MarketingYN === 'Y' ? 'Y' : 'N',
    };
  }

  getValuesToPatch(customer: Customer) {
    return {
      customerNumber: customer.customerNumber,
      customerName: customer.customerName,
      customerVehicleID: 0,
      partsVehicleID: 0,
      vehicle: ' ',
      vehicleColour: '',
      quickSaleYN: 'N',
      customerPaymentTerms: customer.customerPaymentTerms,
      customerTaxRate: customer.customerTaxRate,
      shipToNumber: customer.customerNumber,
      shipToName: customer.customerName,
    };
  }

  openModalBox() {
    this.modalService
      .open(this.modalBox, {
        windowClass: 'garage-modal',
        size: 'xl',
        backdrop: 'static',
      })
      .result.then(
        (_) => {
          this.isAddingCustomer = false;
          this.isSelectingCustomer = false;
          this.isGarage = false;
          this.isEditingCustomer = false;
          this.isEditingShiptoCustomer = false;
        },
        (_) => {
          this.isAddingCustomer = false;
          this.isSelectingCustomer = false;
          this.isGarage = false;
          this.isEditingCustomer = false;
          this.isEditingShiptoCustomer = false;
        }
      );
  }

  isNonCorpOrder() {
    return this.orderHeader?.nonCorpStoreSaleYN === 'Y';
  }

  switchShowOrder() {
    this.form.patchValue(
      {
        orderType: this.convertOrderType(this.isNonCorpOrder(), this.form.controls.orderType.value),
        nonCorpStoreSaleYN: this.isNonCorpOrder() ? 'N' : 'Y',
        shipToNumber: this.isNonCorpOrder() ? this.orderHeader.branch : ' ',
        shipToName: this.isNonCorpOrder() ? this.branchList.find((branch) => branch.BranchCode === this.orderHeader.branch)?.BranchDescription : ' ',
      },
      { emitEvent: false }
    );
    this.updateHeaders();
  }

  convertOrderType(isNonCorpOrder: boolean, orderType: string) {
    return isNonCorpOrder && orderType === ORDER_TYPE.showSaleOrder
      ? ORDER_TYPE.saleOrder
      : !isNonCorpOrder && orderType === ORDER_TYPE.saleOrder
      ? ORDER_TYPE.showSaleOrder
      : this.nonCorpOrderTypeList.find((item) => item.OrderTypeCode === orderType)
      ? orderType
      : ' ';
  }

  handleCloseModal(event) {
    if (event) {
      if (this.isGenericVehicleYN && this.isAddVehicleForGenericCustomer) {
        this.form.patchValue(
          {
            customerVehicleID: 0,
            partsVehicleID: 0,
            vehicle: '',
          },
          { emitEvent: false }
        );
      }
      this.modalService.dismissAll();
    }
  }

  //Add validation to Reference field
  addRemoveValidationFromReference(customer: CustomerDetails, emitEvent = true) {
    if (customer?.CustomerType === CUSTOMER_TYPE.retail || customer?.referenceRequiredYN === 'N') {
      this.isOrderReferenceRequired = false;
      this.form.get('reference').clearValidators();
    } else if (customer?.referenceRequiredYN === 'Y') {
      if (this.form.get('reference').value?.length === 0) {
        this.isOrderReferenceRequired = true;
      }
      this.form.get('reference').addValidators([Validators.required, Validators.pattern(/^(\s+\S+\s*)*(?!\s).*$/), Validators.minLength(1)]);
    }
    this.form.get('reference').updateValueAndValidity({ emitEvent: true });
  }

  //Check Reference is required or not
  isReferenceRequired(referenceRequiredYN: string) {
    if (referenceRequiredYN == 'Y') {
      return true;
    }
    return false;
  }

  //TODO:remove reference as hardcoded
  //Generate Generic Empty field error message toastr
  createEmptyFieldErrorMessage(value: string, key: string) {
    if (value.trim() === '') {
      this.form.controls.reference.setErrors({
        invalid: true,
      });
      this.isOrderReferenceRequired = true;
      let errorKey = key.substring(0, 1).toUpperCase();
      errorKey = errorKey + key.substring(1);
      if (this.orderHeader?.reference) {
        this.toastr.error(`${errorKey} field cannot be empty, please provide value.`);
      }
    } else {
      this.isOrderReferenceRequired = false;
    }
  }

  //Check if validation exist or not
  isValidatorExist(field: string) {
    const formField = this.form.get(field);
    if (!formField.validator) {
      return false;
    }

    const validator = formField.validator({} as AbstractControl);
    return validator && validator.required;
  }

  private updateBranchList(): void {
    //fetchcustomer details for branch
    this.apiService
      .fetchCustomerDetails({
        customerNumber: this.orderHeader?.customerNumber,
      })
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((res) => {
        const customer = res.SearchResults[0];
        if (customer.allowedBranches || customer.allowedNonCorps) {
          this.allAllowedBranchesList = getCombinedBranches(customer.allowedBranches, customer.allowedNonCorps);
          this.form.controls['shipToNumber'].setValue(this.orderHeader.shipToNumber.toString(), { emitEvent: false });
          if (this.allAllowedBranchesList.length === 0) {
            this.allAllowedBranchesList.push({
              BranchCode: this.orderHeader.shipToNumber.toString(),
              BranchDescription: this.orderHeader.shipToName,
            });
          }
          // else if(!this.allAllowedBranchesList.some(el=>el.BranchCode==this.orderHeader.shipToNumber.toString())){
          //   this.allAllowedBranchesList.push({
          //     BranchCode: this.orderHeader.shipToNumber.toString(),
          //     BranchDescription: this.orderHeader.shipToName,
          //   });
          // }
        }
      });
  }

  private updateNonCorpStoreList(): void {
    if (!this.nonCorpStoreList.some((item) => +item.customerNumber === this.orderHeader?.shipToNumber)) {
      this.nonCorpStoreList.push({
        customerNumber: this.orderHeader?.shipToNumber.toString(),
        customerName: this.orderHeader?.shipToName,
      } as any);
      this.form.controls['shipToNumber'].setValue(this.orderHeader.shipToNumber.toString(), { emitEvent: false });
      this.form.controls['shipToName'].setValue(this.orderHeader.shipToName, { emitEvent: false });
    }
  }

  showDetails = (d: NgbDateStruct) => {
    let date: any;
    let month: any;
    if (d.day?.toString().length < 2) {
      date = '0' + d.day.toString();
    } else {
      date = d.day.toString();
    }
    if (d.month.toString().length < 2) {
      month = '0' + d.month.toString();
    } else {
      month = d.month.toString();
    }
    const day = date + '/' + month + '/' + d.year.toString();
    if (this.previousDate === day) {
      this.d2.close();
      this.router.navigate(['/pos/scheduler/' + day + '/timeline']);
    } else {
      this.previousDate = day;
    }
    setTimeout(() => {
      //setting up for click within 500ms then considered as double click else ignore as double click
      this.previousDate = null;
      // auto close
      if (!this.orderHeader?.workshopBookingTF) {
        this.d2.close();
      }
    }, 500);

    //add and remove selected dates
    if (this.orderHeader?.workshopBookingTF) {
      if (this.selectedFitDays?.filter((el) => el === day).length < 1 && this.dayDetails.filter((el) => el.date === day).length > 0) {
        this.selectedFitDays = [...this.selectedFitDays, day];
      } else {
        this.selectedFitDays = this.selectedFitDays?.filter((el) => el !== day);
      }
      //select earliest date and add to request date field
      if (this.selectedFitDays.length > 0) {
        this.selectedFitDays.sort((a, b) => moment(a, 'DD/MM/YYYY').diff(moment(b, 'DD/MM/YYYY')));
        this.form.patchValue({
          requestDate: this.selectedFitDays[0],
        });
      } else {
        this.form.patchValue({
          requestDate: this.latestATPDateValid,
        });
        this.form.get('requestDate').setErrors({ invalid: true }, { emitEvent: false });
      }
      this.displayMonth(this.dayDetails, this?.fitDays, this?.fitHours);
    }
  };

  fetchDayDetails(event) {}
  streamOpened(event) {
    if (this.orderHeader?.workshopBookingTF) {
      document.querySelectorAll('.ngb-dp-month').forEach((el) => {
        el.className = 'ngb-dp-month-custom';
      });
      document.querySelectorAll('.ngb-dp-weekdays').forEach((el) => {
        el.className = 'ngb-dp-week ngb-dp-weekdays ng-star-inserted ngb-dp-weekdays-custom';
      });
      document.querySelectorAll('.ngb-dp-week').forEach((el, i) => {
        if (i === 0) {
          el.className = 'ngb-dp-week ng-star-inserted ngb-dp-week-label-custom';
        } else {
          el.className = 'ngb-dp-week ng-star-inserted ngb-dp-week-custom';
        }
      });
      document.querySelectorAll('.ngb-dp-day.ng-star-inserted').forEach((el) => {
        el.className = 'ngb-dp-day ng-star-inserted ngb-dp-day-custom';
      });
      let x = document.querySelectorAll('.ngb-dp-day');
      if (this.dateToString(x[0]?.getAttribute('aria-label')) && this.dateToString(x[x.length - 1]?.getAttribute('aria-label'))) {
        this.fetchMonthlyViewForOrder(
          this.dateToString(x[0]?.getAttribute('aria-label')),
          this.dateToString(x[x.length - 1]?.getAttribute('aria-label'))
        );
      }
    } else {
      document.querySelectorAll('.ngb-dp-weekdays').forEach((el) => {
        el.classList.add('ngb-dp-weekdays-defaultMatch');
      });
      document.querySelectorAll('.ngb-dp-week').forEach((el) => {
        el.classList.add('ngb-dp-weekdays-defaultMatch');
      });

      document.querySelectorAll('.ngb-dp-day').forEach((el) => {
        el.classList.add('ngb-dp-weekdays-defaultMatch');
      });
    }
  }
  navigateCalendar(event) {
    this.currentMonth = event.next.month;
    if (this.orderHeader?.workshopBookingTF) {
      let x = document.querySelectorAll('.ngb-dp-day');
      setTimeout(() => {
        if (x[0]?.getAttribute('aria-label') && x[x.length - 1]?.getAttribute('aria-label')) {
          this.fetchMonthlyViewForOrder(
            this.dateToString(x[0]?.getAttribute('aria-label')),
            this.dateToString(x[x.length - 1]?.getAttribute('aria-label'))
          );
        }
      }, 100);
    }
  }
  fetchMonthlyViewForOrder(startDate, endDate) {
    this.apiService
      .fetchMonthlyViewForOrder({
        startDate: startDate,
        endDate: endDate,
        orderNumber: this.orderHeader.orderNumber,
        branch: this.orderHeader.branch,
      })
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((res: any) => {
        if (res) {
          this.dayDetails = res?.dates;
          this.fitDays = res?.fitDays;
          this.fitHours = res?.fitHours;
          setTimeout(() => {
            this.displayMonth(res?.dates, res?.fitDays, res?.fitHours);
          }, 100);
        }
      });
  }
  displayMonth(dates, fitDays, fitHours) {
    //remove latest custom Header on calendar
    if (document.querySelectorAll('.ngb-dp-week-label-custom-header').length > 0) {
      const elements = document.querySelectorAll('.ngb-dp-week-label-custom-header');
      elements.forEach((element) => {
        element.remove();
      });
    }
    //add updated custom Header on calendar
    const elements = document.querySelectorAll('.ngb-dp-header');
    elements.forEach((element) => {
      let child = document.createElement('div');
      child.setAttribute('style', 'white-space: pre;');
      const newContent = document.createTextNode('FIT HOURS : ' + fitHours);
      const newContent2 = document.createTextNode('SUGGESTED FIT DAYS : ' + fitDays);
      const newContent3 = document.createTextNode('DAYS SELECTED : ' + this.selectedFitDays.length);
      child.appendChild(newContent);
      child.appendChild(document.createTextNode('\n'));
      child.appendChild(newContent2);
      child.appendChild(document.createTextNode('\n'));
      child.appendChild(newContent3);
      child.classList.add('ngb-dp-week-label-custom-header');
      this.renderer.insertBefore(element, child, element.childNodes[0]);
    });

    let x = document?.querySelectorAll('.ngb-dp-day');
    x?.forEach((y) => {
      if (y.getAttribute('aria-label')) {
        const dateSearch = this.dateToString(y.getAttribute('aria-label'));
        if (Number(dateSearch.split('/')[1]) === this.currentMonth) {
          const data = dates.find((f) => f.date === dateSearch);
          if (data) {
            if (data?.ATPErrorTF) {
              document.getElementById(y.children[0].children[0].id).className = 'atpErrorTFCss';
            } else if (data?.fullyBookedTF) {
              document.getElementById(y.children[0].children[0].id).className = 'fullyBookedTFCss';
            } else if (data?.partialSpaceTF) {
              document.getElementById(y.children[0].children[0].id).className = 'partialSpaceTFCss';
            } else if (data?.spaceAvailable) {
              document.getElementById(y.children[0].children[0].id).className = 'spaceAvailableCss';
            }
            document.getElementById(y.children[0].children[1].id).innerHTML =
              (data.totalHoursAvail - data?.bookedHours).toFixed(2).toString() + ' hrs';
            document
              .getElementById(y.children[0].children[0].id)
              .setAttribute('title', `Booked Hrs: ${data?.bookedHours}\nBooked: ${data?.bookedPerc}%`);
          } else {
            document.getElementById(y.children[0].children[1].id).innerHTML = '';
            y.classList.add('customCss');
            document.getElementById(y.children[0].children[0].id).className = 'disabled';
          }
          const selectedDaysData = this.selectedFitDays.find((f) => f == dateSearch);
          if (selectedDaysData) {
            document.getElementById(y.children[0].children[0].id).className = 'selectedFittingDateCss';
          }
        }
      }
    });
  }

  isDisabled = (date: NgbDateStruct, current: { month: number; year: number }) => {
    return current.month !== date.month;
  };
  dateToString(date: any) {
    let d = new Date(date),
      month = '' + (d.getMonth() + 1),
      day = '' + d.getDate(),
      year = d.getFullYear();

    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;
    return [day, month, year].join('/');
  }

  filterAndSelectDatesWithText(datesWithText) {
    if (datesWithText.length === 1) {
      return datesWithText[0];
    } else {
      const dateToText = {};
      // Iterate over each date with text
      for (const dateText of datesWithText) {
        const [date, text] = dateText.split(' - ');
        const dateIso = date.split('/').reverse().join('-');
        // Update the dictionary with the highest text for each date
        if (!(dateIso in dateToText)) {
          dateToText[dateIso] = text;
        } else {
          if (text === 'HIGH') {
            dateToText[dateIso] = text;
          } else if (text === 'MEDIUM' && dateToText[dateIso] !== 'HIGH') {
            dateToText[dateIso] = text;
          } else if (text === 'LOW' && dateToText[dateIso] !== 'HIGH' && dateToText[dateIso] !== 'MEDIUM') {
            dateToText[dateIso] = text;
          }
        }
      }
      // Find the highest date
      const highestDate = Object.keys(dateToText).reduce((a, b) => (a > b ? a : b));
      // Return the highest date and its corresponding text in the original format
      return highestDate.split('-').reverse().join('/') + ' - ' + dateToText[highestDate];
    }
  }
  checkAllowEditable(event) {
    if (this.orderHeader?.workshopBookingTF) {
      event.preventDefault();
      return false;
    }
  }
  updateHeaderDate() {
    if (this.orderHeader?.workshopBookingTF === true) {
      this.isHeaderValid.emit(this.form.valid);
      this.updateHeaders();
    }
    this.d2.close();
  }

  changeSalesPerson(): void {
    this.customerStore.dispatch(new GetUserSalesPersonListAction());

    this.customerStore
      .select(customersQuery.getUserSalespersonList)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((data) => {
        if (data) {
          this.salesPeopleList = data;
          if (this.salesPeopleList?.length > 0) {
            this.modalService.open(this.changeSalespersonContent, {
              size: 'xl',
              backdrop: 'static',
            });
          }
        }
      });
  }

  reloadData() {
    this.updateHeaders(null, true, false);
    this.modalService.dismissAll();
  }
  closeModal() {
    this.modalService.dismissAll();
  }
  getSalesPersonNameOnly(salesPersonName) {
    //salsesPersoneName consist ~followed by branch code
    //so filter out only name from it
    if (salesPersonName && salesPersonName.length > 0) {
      const salesPerson = salesPersonName.split('~')[0];
      return salesPerson;
    } else {
      return salesPersonName;
    }
  }
  getOrderTypeDescription(orderType: string): string {
    const orderTypeObj = ORDER_TYPES.find((item) => item.OrderTypeCode === orderType);
    return orderTypeObj ? orderTypeObj.OrderTypeDescription : '';
  }
}
